import { sql } from "drizzle-orm";
import { sqliteTable, text, integer, index } from "drizzle-orm/sqlite-core";

// ============================================================================
// communications — Cross-module communication log (call / email / SMS / LINE
// / meeting / note). Originally email-only; extended in PR A of #213 (Option C)
// to act as the unified communications timeline used by every module.
//
// IMPORTANT (backward compatibility):
//   - `direction` and `status` were previously `notNull`. They are now nullable
//     so non-email types (note, meeting, …) don't have to fabricate values.
//     The existing email-queue / email service code always supplies both, so
//     no writer changes are required.
// ============================================================================
export const communications = sqliteTable("core_communications", {
  id: text("id").primaryKey(), // ULID
  companyId: text("company_id").notNull(),

  // Reference to document
  module: text("module"),
  doctype: text("doctype"),
  documentId: text("document_id"),

  // Communication details
  // Allowed values: "call" | "email" | "sms" | "line" | "meeting" | "note"
  type: text("type").notNull(),
  // "outbound" | "inbound" | null (nullable for note/meeting)
  direction: text("direction"),

  // Email fields
  fromEmail: text("from_email"),
  toEmail: text("to_email"),
  cc: text("cc"),
  bcc: text("bcc"),
  subject: text("subject"),
  bodyHtml: text("body_html"),
  bodyText: text("body_text"),

  // Email queue / delivery status (nullable — only used by email type).
  // "queued" | "sent" | "delivered" | "failed" | "bounced"
  status: text("status"),
  errorMessage: text("error_message"),
  externalId: text("external_id"), // Resend message ID
  sentAt: text("sent_at"),
  deliveredAt: text("delivered_at"),
  openedAt: text("opened_at"),

  // Cross-channel fields (PR A of #213) ---------------------------------------
  phoneNumber: text("phone_number"), // call / SMS / LINE
  // call result: "connected" | "no_answer" | "voicemail" | "busy" | "wrong_number" | "declined"
  outcome: text("outcome"),
  durationSeconds: integer("duration_seconds"), // call / meeting duration
  scheduledAt: text("scheduled_at"), // ISO — meetings, follow-ups
  occurredAt: text("occurred_at"), // ISO — when the event happened
  assignedTo: text("assigned_to"), // userId

  // Metadata
  templateId: text("template_id"),
  retryCount: integer("retry_count").default(0),

  createdAt: text("created_at").notNull(),
  createdBy: text("created_by"),
  updatedAt: text("updated_at")
    .notNull()
    .default(sql`CURRENT_TIMESTAMP`),
}, (table) => [
  index("idx_communications_company_status").on(table.companyId, table.status),
  index("idx_communications_by_doc").on(table.companyId, table.module, table.doctype, table.documentId),
  // Timeline queries (PR A) — most recent communications across the tenant by type.
  index("idx_communications_company_type_occurred").on(
    table.companyId,
    table.type,
    table.occurredAt,
  ),
  // Per-record filtering (PR A) — communications attached to a specific doc by type.
  index("idx_communications_doc_type").on(
    table.companyId,
    table.doctype,
    table.documentId,
    table.type,
  ),
]);

// ============================================================================
// Type exports
// ============================================================================
export type Communication = typeof communications.$inferSelect;
export type NewCommunication = typeof communications.$inferInsert;

// Allowed enum values (PR A) — single source of truth for routes / SDK / UI.
export const COMMUNICATION_TYPES = [
  "call",
  "email",
  "sms",
  "line",
  "meeting",
  "note",
] as const;
export type CommunicationType = (typeof COMMUNICATION_TYPES)[number];

export const COMMUNICATION_DIRECTIONS = ["inbound", "outbound"] as const;
export type CommunicationDirection = (typeof COMMUNICATION_DIRECTIONS)[number];

export const COMMUNICATION_OUTCOMES = [
  "connected",
  "no_answer",
  "voicemail",
  "busy",
  "wrong_number",
  "declined",
] as const;
export type CommunicationOutcome = (typeof COMMUNICATION_OUTCOMES)[number];
